/**
 * CampaignReadEndpointPage — CRUD list for the campaign read endpoint.
 *
 * Layout:
 *   1. Top bar: title "Campaigns - Read Endpoint" + "Create" button.
 *   2. Filter bar: search input + status dropdown.
 *   3. Data table: zinc-200 borders, striped rows, loading skeleton, empty, error.
 *   4. Create modal: react-hook-form + zod validation, submit/cancel.
 *
 * Styling: green-600/700/50 accent only, white surface, zinc-200 borders,
 * zinc-900 ink, Inter (inherited), 6-8 px rounded corners, inline SVG icons.
 * No emoji, no emerald, no sky, no blue, no purple, no dark mode, no
 * competitor names.
 */
import { useMemo, useState, type FormEvent } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
	useCampaignReadEndpointList,
	useCampaignReadEndpointCreate,
	useCampaignReadEndpointDelete,
} from "./hooks";
import type { CampaignReadEndpointRow } from "./api";

// ---------------------------------------------------------------------------
// Style tokens - green accent only, zinc borders, 6-8 px radius.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT = INPUT;
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"inline-flex items-center gap-1 rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const BTN_DANGER =
	"inline-flex items-center gap-1 rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 hover:border-red-300 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Icons (inline SVG, no emojis).
// ---------------------------------------------------------------------------

function IconPlus() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2.25"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M12 5v14M5 12h14" />
		</svg>
	);
}

function IconSearch() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<circle cx="11" cy="11" r="7" />
			<path d="m20 20-3.5-3.5" />
		</svg>
	);
}

function IconClose() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M18 6 6 18M6 6l12 12" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Status vocabulary (UI-only filter).
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = ["all", "active", "paused", "archived"] as const;
type StatusFilter = (typeof STATUS_OPTIONS)[number];

function statusLabel(s: StatusFilter): string {
	if (s === "all") return "All statuses";
	return s.charAt(0).toUpperCase() + s.slice(1);
}

function rowStatus(row: CampaignReadEndpointRow): StatusFilter {
	const raw = typeof row["status"] === "string" ? (row["status"] as string) : "active";
	if (raw === "paused" || raw === "archived" || raw === "active") return raw;
	return "active";
}

function statusPillClass(s: StatusFilter): string {
	if (s === "active") {
		return "inline-flex items-center rounded-[6px] border border-green-600 bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700";
	}
	if (s === "paused") {
		return "inline-flex items-center rounded-[6px] border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-[11px] font-semibold text-zinc-700";
	}
	return "inline-flex items-center rounded-[6px] border border-zinc-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-zinc-500";
}

// ---------------------------------------------------------------------------
// Filter helper (exported for tests if ever needed).
// ---------------------------------------------------------------------------

export function applyFilters(
	items: CampaignReadEndpointRow[],
	q: string,
	status: StatusFilter,
): CampaignReadEndpointRow[] {
	const needle = q.trim().toLowerCase();
	return items.filter((row) => {
		if (status !== "all" && rowStatus(row) !== status) return false;
		if (!needle) return true;
		const name = typeof row["name"] === "string" ? (row["name"] as string) : "";
		const id = typeof row.id === "string" ? row.id : "";
		return (
			name.toLowerCase().includes(needle) ||
			id.toLowerCase().includes(needle)
		);
	});
}

// ---------------------------------------------------------------------------
// Create modal form schema.
// ---------------------------------------------------------------------------

const createSchema = z.object({
	name: z
		.string()
		.min(1, "Name is required.")
		.max(120, "Name must be 120 characters or fewer."),
	status: z.enum(["active", "paused", "archived"]),
});
type CreateValues = z.infer<typeof createSchema>;

// ---------------------------------------------------------------------------
// Table sub-components.
// ---------------------------------------------------------------------------

function SkeletonRows() {
	return (
		<>
			{[0, 1, 2, 3].map((i) => (
				<tr
					key={i}
					className="border-b border-zinc-100 last:border-0"
					data-testid="campaign-read-endpoint-skeleton-row"
				>
					<td className="px-4 py-3">
						<div className="h-3 w-40 animate-pulse rounded bg-zinc-100" />
					</td>
					<td className="px-4 py-3">
						<div className="h-3 w-20 animate-pulse rounded bg-zinc-100" />
					</td>
					<td className="px-4 py-3">
						<div className="h-3 w-24 animate-pulse rounded bg-zinc-100" />
					</td>
					<td className="px-4 py-3 text-right">
						<div className="ml-auto h-3 w-16 animate-pulse rounded bg-zinc-100" />
					</td>
				</tr>
			))}
		</>
	);
}

function DataRow(props: {
	row: CampaignReadEndpointRow;
	onDelete: (id: string) => void;
	deleting: boolean;
}) {
	const { row, onDelete, deleting } = props;
	const st = rowStatus(row);
	const name = typeof row["name"] === "string" ? (row["name"] as string) : "-";
	const updated = row.updated_at
		? new Date(row.updated_at * 1000).toLocaleString(undefined, {
				year: "numeric",
				month: "short",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
			})
		: "-";
	return (
		<tr
			className="border-b border-zinc-100 last:border-0 odd:bg-white even:bg-zinc-50/60 hover:bg-green-50/40"
			data-testid={`campaign-read-endpoint-row-${row.id}`}
		>
			<td className="px-4 py-3 text-sm text-zinc-900">
				<div className="flex flex-col">
					<span className="font-medium">{name}</span>
					<span className="font-mono text-[11px] text-zinc-500">
						{row.id}
					</span>
				</div>
			</td>
			<td className="px-4 py-3 text-sm">
				<span
					className={statusPillClass(st)}
					data-testid={`campaign-read-endpoint-status-${row.id}`}
				>
					{statusLabel(st)}
				</span>
			</td>
			<td className="px-4 py-3 text-sm tabular-nums text-zinc-600">
				{updated}
			</td>
			<td className="px-4 py-3 text-right">
				<button
					type="button"
					className={BTN_DANGER}
					onClick={() => onDelete(row.id)}
					disabled={deleting}
					data-testid={`campaign-read-endpoint-delete-${row.id}`}
				>
					Delete
				</button>
			</td>
		</tr>
	);
}

// ---------------------------------------------------------------------------
// Create modal.
// ---------------------------------------------------------------------------

function CreateModal(props: {
	open: boolean;
	submitting: boolean;
	error: string | null;
	onCancel: () => void;
	onSubmit: (values: CreateValues) => void;
}) {
	const { open, submitting, error, onCancel, onSubmit } = props;
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CreateValues>({
		resolver: zodResolver(createSchema),
		defaultValues: { name: "", status: "active" },
	});

	if (!open) return null;

	const submit = (values: CreateValues) => {
		onSubmit(values);
	};

	return (
		<div
			className="fixed inset-0 z-40 flex items-start justify-center bg-zinc-900/40 px-4 py-10"
			role="dialog"
			aria-modal="true"
			aria-labelledby="campaign-read-endpoint-create-title"
			data-testid="campaign-read-endpoint-modal"
		>
			<div className="w-full max-w-md rounded-[8px] border border-zinc-200 bg-white shadow-xl">
				<header className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
					<h2
						id="campaign-read-endpoint-create-title"
						className="text-base font-semibold text-zinc-900"
					>
						Create campaign
					</h2>
					<button
						type="button"
						className="rounded-[6px] p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
						onClick={() => {
							reset();
							onCancel();
						}}
						aria-label="Close"
						data-testid="campaign-read-endpoint-modal-close"
					>
						<IconClose />
					</button>
				</header>
				<form
					onSubmit={handleSubmit(submit)}
					className="flex flex-col gap-4 px-5 py-4"
					data-testid="campaign-read-endpoint-create-form"
				>
					<label className="flex flex-col gap-1">
						<span className="text-xs font-medium text-zinc-600">
							Name
						</span>
						<input
							type="text"
							className={INPUT}
							placeholder="Summer promo"
							autoFocus
							data-testid="campaign-read-endpoint-name-input"
							{...register("name")}
						/>
						{errors.name ? (
							<span
								className="text-xs text-red-600"
								role="alert"
								data-testid="campaign-read-endpoint-name-error"
							>
								{errors.name.message}
							</span>
						) : null}
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-xs font-medium text-zinc-600">
							Status
						</span>
						<select
							className={SELECT}
							data-testid="campaign-read-endpoint-status-input"
							{...register("status")}
						>
							<option value="active">Active</option>
							<option value="paused">Paused</option>
							<option value="archived">Archived</option>
						</select>
					</label>
					{error ? (
						<p
							className="rounded-[6px] border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
							role="alert"
							data-testid="campaign-read-endpoint-modal-error"
						>
							{error}
						</p>
					) : null}
					<div className="flex justify-end gap-2 border-t border-zinc-200 pt-3">
						<button
							type="button"
							className={BTN_GHOST}
							onClick={() => {
								reset();
								onCancel();
							}}
							disabled={submitting}
							data-testid="campaign-read-endpoint-cancel"
						>
							Cancel
						</button>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={submitting}
							data-testid="campaign-read-endpoint-submit"
						>
							{submitting ? "Saving." : "Create"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------------

export function CampaignReadEndpointPage() {
	const listQuery = useCampaignReadEndpointList();
	const createMut = useCampaignReadEndpointCreate();
	const deleteMut = useCampaignReadEndpointDelete();

	const [q, setQ] = useState<string>("");
	const [status, setStatus] = useState<StatusFilter>("all");
	const [modalOpen, setModalOpen] = useState<boolean>(false);

	const items = listQuery.data?.items ?? [];
	const filtered = useMemo(() => applyFilters(items, q, status), [items, q, status]);

	const handleCreate = (values: CreateValues) => {
		createMut.mutate(
			{ name: values.name, status: values.status },
			{
				onSuccess: () => {
					setModalOpen(false);
				},
			},
		);
	};

	const handleReset = (e: FormEvent) => {
		e.preventDefault();
		setQ("");
		setStatus("all");
	};

	return (
		<main
			data-testid="campaign-read-endpoint-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-6 py-8"
		>
			{/* -------------------------- top bar -------------------------- */}
			<header
				className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
				data-testid="campaign-read-endpoint-topbar"
			>
				<div className="flex flex-col gap-1">
					<p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
						campaign-read-endpoint
					</p>
					<h1 className="text-2xl font-semibold text-zinc-900">
						Campaigns - Read Endpoint
					</h1>
					<p className="text-sm text-zinc-600">
						Browse campaign records exposed by the read endpoint for this
						tenant.
					</p>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={() => {
							createMut.reset();
							setModalOpen(true);
						}}
						data-testid="campaign-read-endpoint-open-create"
					>
						<IconPlus />
						Create
					</button>
				</div>
			</header>

			{/* -------------------------- filter bar -------------------------- */}
			<section
				className={`${CARD} px-4 py-3`}
				aria-label="Filters"
				data-testid="campaign-read-endpoint-filters"
			>
				<form
					onSubmit={(e) => e.preventDefault()}
					className="flex flex-col gap-3 sm:flex-row sm:items-end"
					data-testid="campaign-read-endpoint-filter-form"
				>
					<label className="flex flex-1 flex-col gap-1">
						<span className="text-xs font-medium text-zinc-600">
							Search
						</span>
						<div className="relative">
							<span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400">
								<IconSearch />
							</span>
							<input
								type="text"
								className={`${INPUT} pl-8`}
								placeholder="Search by name or id"
								value={q}
								onChange={(e) => setQ(e.currentTarget.value)}
								data-testid="campaign-read-endpoint-search-input"
							/>
						</div>
					</label>
					<label className="flex flex-col gap-1 sm:w-56">
						<span className="text-xs font-medium text-zinc-600">
							Status
						</span>
						<select
							className={SELECT}
							value={status}
							onChange={(e) => setStatus(e.currentTarget.value as StatusFilter)}
							data-testid="campaign-read-endpoint-status-filter"
						>
							{STATUS_OPTIONS.map((s) => (
								<option key={s} value={s}>
									{statusLabel(s)}
								</option>
							))}
						</select>
					</label>
					<div className="flex gap-2 sm:pb-[2px]">
						<button
							type="button"
							className={BTN_GHOST}
							onClick={handleReset}
							data-testid="campaign-read-endpoint-filter-reset"
						>
							Reset
						</button>
					</div>
				</form>
			</section>

			{/* -------------------------- table -------------------------- */}
			<section
				className={CARD}
				aria-label="Campaigns table"
				data-testid="campaign-read-endpoint-table-card"
			>
				<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
					<h2 className="text-sm font-semibold text-zinc-900">
						{filtered.length} record{filtered.length === 1 ? "" : "s"}
					</h2>
					{listQuery.isFetching && !listQuery.isLoading ? (
						<span
							className="text-xs text-zinc-500"
							data-testid="campaign-read-endpoint-refetching"
						>
							Refreshing.
						</span>
					) : null}
				</div>
				<div className="overflow-x-auto">
					<table
						className="w-full border-collapse text-left"
						data-testid="campaign-read-endpoint-table"
					>
						<thead>
							<tr className="border-b border-zinc-200 bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
								<th className="px-4 py-2 font-semibold">Name</th>
								<th className="px-4 py-2 font-semibold">Status</th>
								<th className="px-4 py-2 font-semibold">Updated</th>
								<th className="px-4 py-2 text-right font-semibold">
									Actions
								</th>
							</tr>
						</thead>
						<tbody data-testid="campaign-read-endpoint-tbody">
							{listQuery.isLoading ? (
								<>
									<tr>
										<td
											colSpan={4}
											className="hidden"
											data-testid="campaign-read-endpoint-loading"
										>
											Loading.
										</td>
									</tr>
									<SkeletonRows />
								</>
							) : listQuery.error ? (
								<tr>
									<td
										colSpan={4}
										className="px-4 py-10 text-center text-sm text-red-600"
										role="alert"
										data-testid="campaign-read-endpoint-error"
									>
										{listQuery.error.message || "Failed to load campaigns."}
									</td>
								</tr>
							) : filtered.length === 0 ? (
								<tr>
									<td
										colSpan={4}
										className="px-4 py-10 text-center text-sm text-zinc-500"
										data-testid="campaign-read-endpoint-empty"
									>
										{items.length === 0
											? "No campaigns yet. Click Create to add one."
											: "No campaigns match the current filters."}
									</td>
								</tr>
							) : (
								filtered.map((row) => (
									<DataRow
										key={row.id}
										row={row}
										onDelete={(id) => deleteMut.mutate(id)}
										deleting={deleteMut.isPending}
									/>
								))
							)}
						</tbody>
					</table>
				</div>
			</section>

			<CreateModal
				open={modalOpen}
				submitting={createMut.isPending}
				error={createMut.error ? createMut.error.message : null}
				onCancel={() => setModalOpen(false)}
				onSubmit={handleCreate}
			/>
		</main>
	);
}

export default CampaignReadEndpointPage;
